import React from 'react';
import { motion } from 'framer-motion';
import {
  CurrencyDollarIcon,
  FireIcon,
  LockClosedIcon,
  ChartBarIcon,
  UserGroupIcon,
  ArrowTrendingUpIcon,
  GiftIcon,
  ShieldCheckIcon,
  BuildingLibraryIcon,
  SparklesIcon,
  RocketLaunchIcon,
  CubeTransparentIcon,
} from '@heroicons/react/24/outline';

const TokenomicsPage: React.FC = () => {
  const tokenStats = {
    totalSupply: '1,000,000,000',
    circulatingSupply: '450,000,000',
    marketCap: '$125,000,000',
    price: '$0.278',
    priceChange24h: 5.8,
    totalStaked: '320,000,000',
    totalBurned: '15,234,567',
    holders: '45,892',
  };

  const distribution = [
    { label: 'Community Rewards', percentage: 40, color: 'bg-primary-500', description: 'Staking, governance participation, and platform engagement' },
    { label: 'Team & Advisors', percentage: 15, color: 'bg-purple-500', description: '4-year vesting with 1-year cliff' },
    { label: 'Treasury', percentage: 20, color: 'bg-green-500', description: 'DAO-governed funds for ecosystem development' },
    { label: 'Liquidity Mining', percentage: 10, color: 'bg-amber-500', description: 'LP incentives across DEXs' },
    { label: 'Private Sale', percentage: 10, color: 'bg-blue-500', description: '2-year vesting schedule' },
    { label: 'Public Sale', percentage: 5, color: 'bg-red-500', description: 'Initial distribution event' },
  ];

  const utilities = [
    {
      icon: LockClosedIcon,
      title: 'Staking Rewards',
      description: 'Earn 15-30% APY by staking NDH tokens. Higher tiers unlock better rewards and exclusive features.',
      color: 'text-green-400',
      bgColor: 'bg-green-500/10',
    },
    {
      icon: BuildingLibraryIcon,
      title: 'Governance',
      description: '1 NDH = 1 Vote. Participate in protocol decisions, parameter changes, and treasury allocation.',
      color: 'text-purple-400',
      bgColor: 'bg-purple-500/10',
    },
    {
      icon: CurrencyDollarIcon,
      title: 'Fee Discounts',
      description: 'Reduce platform fees by up to 100% based on your staking tier. Platinum stakers pay 0% fees.',
      color: 'text-primary-400',
      bgColor: 'bg-primary-500/10',
    },
    {
      icon: SparklesIcon,
      title: 'VIP Access',
      description: 'Early access to auctions, exclusive drops, whitelist spots, and premium features.',
      color: 'text-amber-400',
      bgColor: 'bg-amber-500/10',
    },
    {
      icon: ShieldCheckIcon,
      title: 'Curation Staking',
      description: 'Stake on collections you believe in. Earn rewards when your picks perform well.',
      color: 'text-blue-400',
      bgColor: 'bg-blue-500/10',
    },
    {
      icon: ChartBarIcon,
      title: 'Liquidity Mining',
      description: 'Provide liquidity to NDH pairs and earn additional token rewards on top of trading fees.',
      color: 'text-red-400',
      bgColor: 'bg-red-500/10',
    },
    {
      icon: FireIcon,
      title: 'Buyback & Burn',
      description: '30% of platform fees used to buy back and burn NDH, reducing supply over time.',
      color: 'text-orange-400',
      bgColor: 'bg-orange-500/10',
    },
  ];

  const vestingSchedule = [
    { category: 'Community Rewards', cliff: 'None', vesting: 'Released over 4 years', tge: '10%' },
    { category: 'Team & Advisors', cliff: '12 months', vesting: '48 months linear', tge: '0%' },
    { category: 'Treasury', cliff: 'None', vesting: 'DAO controlled', tge: '20%' },
    { category: 'Liquidity Mining', cliff: 'None', vesting: '36 months', tge: '5%' },
    { category: 'Private Sale', cliff: '6 months', vesting: '24 months linear', tge: '0%' },
    { category: 'Public Sale', cliff: 'None', vesting: '12 months', tge: '25%' },
  ];

  return (
    <div className="min-h-screen py-8">
      <div className="container-custom">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center space-x-2 px-4 py-2 rounded-full bg-primary-500/10 text-primary-400 mb-6">
            <CubeTransparentIcon className="h-5 w-5" />
            <span className="font-medium">NDH Token</span>
          </div>
          <h1 className="text-4xl md:text-6xl font-bold mb-6">
            <span className="text-gradient">NARDIHA Token</span>
          </h1>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            The native utility token powering the TEMPLUM DAO ecosystem.
            Stake, govern, and unlock exclusive benefits.
          </p>
        </motion.div>

        {/* Token Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-16"
        >
          {[
            { label: 'Price', value: tokenStats.price, change: `+${tokenStats.priceChange24h}%`, changeColor: 'text-green-400' },
            { label: 'Market Cap', value: tokenStats.marketCap },
            { label: 'Circulating Supply', value: tokenStats.circulatingSupply },
            { label: 'Total Staked', value: tokenStats.totalStaked },
          ].map((stat, index) => (
            <div key={index} className="glass-card p-6 text-center">
              <p className="text-sm text-gray-400 mb-2">{stat.label}</p>
              <p className="text-2xl md:text-3xl font-bold">{stat.value}</p>
              {stat.change && (
                <p className={`text-sm ${stat.changeColor}`}>{stat.change}</p>
              )}
            </div>
          ))}
        </motion.div>

        {/* Token Distribution */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-16"
        >
          <h2 className="text-3xl font-bold mb-8 text-center">Token Distribution</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Chart */}
            <div className="glass-card p-8">
              <div className="relative w-64 h-64 mx-auto mb-8">
                {/* Simple pie chart representation */}
                <svg viewBox="0 0 100 100" className="transform -rotate-90 w-full h-full">
                  {distribution.reduce((acc, item, index) => {
                    const startAngle = acc.offset;
                    const angle = (item.percentage / 100) * 360;
                    const endAngle = startAngle + angle;

                    const x1 = 50 + 40 * Math.cos((startAngle * Math.PI) / 180);
                    const y1 = 50 + 40 * Math.sin((startAngle * Math.PI) / 180);
                    const x2 = 50 + 40 * Math.cos((endAngle * Math.PI) / 180);
                    const y2 = 50 + 40 * Math.sin((endAngle * Math.PI) / 180);

                    const largeArc = angle > 180 ? 1 : 0;

                    const colors = ['#00D1FF', '#A855F7', '#22C55E', '#F59E0B', '#3B82F6', '#EF4444'];

                    acc.paths.push(
                      <path
                        key={index}
                        d={`M 50 50 L ${x1} ${y1} A 40 40 0 ${largeArc} 1 ${x2} ${y2} Z`}
                        fill={colors[index]}
                        className="opacity-80 hover:opacity-100 transition-opacity cursor-pointer"
                      />
                    );

                    acc.offset = endAngle;
                    return acc;
                  }, { paths: [] as JSX.Element[], offset: 0 }).paths}
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center">
                    <p className="text-3xl font-bold">1B</p>
                    <p className="text-sm text-gray-400">Total Supply</p>
                  </div>
                </div>
              </div>

              {/* Legend */}
              <div className="grid grid-cols-2 gap-3">
                {distribution.map((item, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <div className={`w-3 h-3 rounded-full ${item.color}`} />
                    <span className="text-sm text-gray-400">{item.label}</span>
                    <span className="text-sm font-semibold">{item.percentage}%</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Distribution Details */}
            <div className="space-y-4">
              {distribution.map((item, index) => (
                <div key={index} className="glass-card p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-3">
                      <div className={`w-4 h-4 rounded-full ${item.color}`} />
                      <span className="font-semibold">{item.label}</span>
                    </div>
                    <span className="text-lg font-bold">{item.percentage}%</span>
                  </div>
                  <p className="text-sm text-gray-400 ml-7">{item.description}</p>
                  <div className="mt-3 w-full h-2 bg-dark-700 rounded-full overflow-hidden">
                    <div
                      className={`h-full ${item.color} rounded-full`}
                      style={{ width: `${item.percentage}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Token Utilities */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mb-16"
        >
          <h2 className="text-3xl font-bold mb-8 text-center">Token Utilities</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {utilities.map((utility, index) => {
              const Icon = utility.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 * index }}
                  className="glass-card p-6 hover:border-primary-500/50 transition-all duration-300"
                >
                  <div className={`w-14 h-14 rounded-xl ${utility.bgColor} flex items-center justify-center mb-4`}>
                    <Icon className={`h-7 w-7 ${utility.color}`} />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">{utility.title}</h3>
                  <p className="text-gray-400">{utility.description}</p>
                </motion.div>
              );
            })}
          </div>
        </motion.div>

        {/* Vesting Schedule */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mb-16"
        >
          <h2 className="text-3xl font-bold mb-8 text-center">Vesting Schedule</h2>
          <div className="glass-card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Category</th>
                    <th className="px-6 py-4 text-center text-sm font-medium text-gray-400">Cliff Period</th>
                    <th className="px-6 py-4 text-center text-sm font-medium text-gray-400">Vesting</th>
                    <th className="px-6 py-4 text-center text-sm font-medium text-gray-400">TGE Unlock</th>
                  </tr>
                </thead>
                <tbody>
                  {vestingSchedule.map((item, index) => (
                    <tr key={index} className="border-b border-white/5">
                      <td className="px-6 py-4 font-medium">{item.category}</td>
                      <td className="px-6 py-4 text-center text-gray-400">{item.cliff}</td>
                      <td className="px-6 py-4 text-center text-gray-400">{item.vesting}</td>
                      <td className="px-6 py-4 text-center">
                        <span className="px-3 py-1 rounded-full bg-primary-500/20 text-primary-400 text-sm">
                          {item.tge}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </motion.div>

        {/* Deflationary Mechanics */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mb-16"
        >
          <h2 className="text-3xl font-bold mb-8 text-center">Deflationary Mechanics</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="glass-card p-8 text-center">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-r from-red-500 to-orange-500 flex items-center justify-center">
                <FireIcon className="h-10 w-10 text-white" />
              </div>
              <h3 className="text-2xl font-bold mb-2">Buyback & Burn</h3>
              <p className="text-gray-400 mb-6">
                30% of all platform fees are used to buy NDH from the open market and burn permanently
              </p>
              <div className="p-4 rounded-xl bg-dark-700/50">
                <p className="text-sm text-gray-400">Total Burned</p>
                <p className="text-2xl font-bold text-orange-400">{tokenStats.totalBurned} NDH</p>
              </div>
            </div>

            <div className="glass-card p-8 text-center">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-r from-blue-500 to-cyan-500 flex items-center justify-center">
                <LockClosedIcon className="h-10 w-10 text-white" />
              </div>
              <h3 className="text-2xl font-bold mb-2">Staking Lock</h3>
              <p className="text-gray-400 mb-6">
                Staked tokens are locked, reducing circulating supply and creating scarcity
              </p>
              <div className="p-4 rounded-xl bg-dark-700/50">
                <p className="text-sm text-gray-400">Currently Staked</p>
                <p className="text-2xl font-bold text-blue-400">32%</p>
              </div>
            </div>

            <div className="glass-card p-8 text-center">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center">
                <ArrowTrendingUpIcon className="h-10 w-10 text-white" />
              </div>
              <h3 className="text-2xl font-bold mb-2">Value Accrual</h3>
              <p className="text-gray-400 mb-6">
                As platform volume grows, buyback pressure increases, driving long-term value
              </p>
              <div className="p-4 rounded-xl bg-dark-700/50">
                <p className="text-sm text-gray-400">Platform Volume (30d)</p>
                <p className="text-2xl font-bold text-purple-400">$45.2M</p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="text-center"
        >
          <div className="glass-card p-12 relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-primary-500/10 to-accent-500/10" />
            <div className="relative">
              <RocketLaunchIcon className="h-16 w-16 mx-auto mb-6 text-primary-400" />
              <h2 className="text-3xl font-bold mb-4">Ready to Get Started?</h2>
              <p className="text-gray-400 mb-8 max-w-lg mx-auto">
                Join thousands of users already benefiting from NDH token utilities.
                Stake, govern, and grow with the TEMPLUM ecosystem.
              </p>
              <div className="flex items-center justify-center space-x-4">
                <a href="/staking" className="btn-primary px-8">
                  Start Staking
                </a>
                <a href="https://uniswap.org" target="_blank" rel="noopener noreferrer" className="btn-secondary px-8">
                  Buy NDH
                </a>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default TokenomicsPage;
